(() => {
  const ROOT_SELECTOR = '[data-hera-kol-root]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SAVE_DATA = Boolean(navigator.connection && navigator.connection.saveData);
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const FEATURED_PLAY_THRESHOLD = 0.35;

  function youtubeId(rawUrl) {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.href);
      const host = url.hostname.replace(/^www\./, '');
      let id = '';
      if (host === 'youtu.be') id = url.pathname.split('/').filter(Boolean)[0] || '';
      if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
        id = url.searchParams.get('v') || '';
        if (!id) {
          const parts = url.pathname.split('/').filter(Boolean);
          if (['shorts', 'embed', 'live'].includes(parts[0])) id = parts[1] || '';
        }
      }
      return /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : '';
    } catch (_) {
      return '';
    }
  }

  function instagramCode(rawUrl) {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.href);
      const host = url.hostname.replace(/^www\./, '');
      if (host !== 'instagram.com') return '';
      const parts = url.pathname.split('/').filter(Boolean);
      if (!['reel', 'reels', 'p', 'tv'].includes(parts[0])) return '';
      return /^[A-Za-z0-9_-]+$/.test(parts[1] || '') ? parts[1] : '';
    } catch (_) {
      return '';
    }
  }

  function youtubeEmbed(id, mode) {
    const url = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('rel', '0');
    if (mode === 'preview') {
      url.searchParams.set('mute', '1');
      url.searchParams.set('controls', '0');
      url.searchParams.set('loop', '1');
      url.searchParams.set('playlist', id);
      url.searchParams.set('disablekb', '1');
    } else {
      url.searchParams.set('controls', '1');
    }
    if (/^https?:$/.test(window.location.protocol)) {
      url.searchParams.set('origin', window.location.origin);
    }
    return url.toString();
  }

  function instagramEmbed(id) {
    return `https://www.instagram.com/reel/${id}/embed/captioned/`;
  }

  function iframeFor(src, title, platform, preview = false) {
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = title;
    iframe.loading = 'eager';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;
    iframe.tabIndex = preview ? -1 : 0;
    if (platform === 'youtube') {
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    } else {
      iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share';
    }
    return iframe;
  }

  function init(root) {
    if (root.dataset.heraKolReady === 'true') return;
    root.dataset.heraKolReady = 'true';

    const rail = root.querySelector('[data-hera-kol-rail]');
    const cards = [...root.querySelectorAll('[data-hera-kol-card]')];
    const featuredCard = cards.find((card) => card.dataset.featured === 'true') || null;
    const featuredAutoplay = root.dataset.featuredAutoplay === 'true' && featuredCard && featuredCard.dataset.hasNative === 'true';
    const featuredToggle = featuredCard && featuredCard.querySelector('[data-hera-kol-preview-toggle]');
    const prev = root.querySelector('[data-hera-kol-prev]');
    const next = root.querySelector('[data-hera-kol-next]');
    const status = root.querySelector('[data-hera-kol-status]');
    const dialog = root.querySelector('[data-hera-kol-dialog]');
    const dialogPanel = dialog && dialog.querySelector('[role="dialog"]');
    const player = dialog && dialog.querySelector('[data-hera-kol-player]');
    const playerActions = dialog && dialog.querySelector('[data-hera-kol-actions]');
    const closeButtons = dialog ? [...dialog.querySelectorAll('[data-hera-kol-close]')] : [];
    let lastFocus = null;
    let activePreview = null;
    let activePreviewReason = '';
    let previewTimer = 0;
    let instagramLoadTimer = 0;
    let scrollFrame = 0;
    let viewportFrame = 0;
    let scrollSettleTimer = 0;
    let resizeTimer = 0;
    let resizeSettleTimer = 0;
    let interactionLayoutTimer = 0;
    let viewportObserver = null;
    let featuredRatio = 0;
    let featuredPaused = false;
    let initialPositioned = false;
    let interactionCard = null;
    let centeredCard = featuredCard || cards[0] || null;

    function sourceFor(card) {
      const rawUrl = card.dataset.playbackUrl || card.dataset.sourceUrl || '';
      const youtube = youtubeId(rawUrl);
      if (youtube) return { platform: 'youtube', id: youtube, rawUrl };
      const instagram = instagramCode(rawUrl);
      if (instagram) return { platform: 'instagram', id: instagram, rawUrl };
      return null;
    }

    function addYouTubePoster(card) {
      if (card.querySelector('.hera-kol__poster')) return;
      const fallback = card.querySelector('[data-hera-kol-poster]');
      const source = sourceFor(card);
      if (!fallback || !source || source.platform !== 'youtube') return;
      const image = document.createElement('img');
      image.className = 'hera-kol__poster';
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.src = `https://i.ytimg.com/vi/${source.id}/hqdefault.jpg`;
      fallback.insertAdjacentElement('afterend', image);
    }

    function updateFeaturedToggle() {
      if (!featuredToggle) return;
      const unavailable = !featuredAutoplay || REDUCED_MOTION.matches || SAVE_DATA;
      featuredToggle.hidden = unavailable;
      if (unavailable) return;
      const isPlaying = activePreview === featuredCard && (
        featuredCard.classList.contains('is-previewing') ||
        featuredCard.classList.contains('is-preview-loading')
      );
      const label = isPlaying ? 'Pause' : 'Play';
      const labelNode = featuredToggle.querySelector('[data-hera-kol-preview-toggle-label]');
      if (labelNode) labelNode.textContent = label;
      featuredToggle.setAttribute('aria-label', `${label} featured muted preview`);
      featuredToggle.setAttribute('aria-pressed', featuredPaused ? 'true' : 'false');
    }

    function stopPreview() {
      window.clearTimeout(previewTimer);
      if (!activePreview) {
        updateFeaturedToggle();
        return;
      }
      const slot = activePreview.querySelector('[data-hera-kol-preview]');
      const video = slot && slot.querySelector('video');
      if (video) video.pause();
      const iframe = slot && slot.querySelector('iframe');
      if (iframe) iframe.src = 'about:blank';
      if (slot) slot.replaceChildren();
      activePreview.classList.remove('is-preview-loading');
      activePreview.classList.remove('is-previewing');
      activePreview = null;
      activePreviewReason = '';
      updateFeaturedToggle();
    }

    function nativeVideoFor(card, mode) {
      const template = card.querySelector('[data-hera-kol-native-template]');
      if (!template) return null;
      const video = template.content.querySelector('video');
      if (!video) return null;
      const clone = video.cloneNode(true);
      clone.preload = 'auto';
      clone.playsInline = true;
      clone.autoplay = true;
      clone.controls = mode === 'modal';
      clone.loop = mode === 'preview';
      clone.muted = mode === 'preview';
      if (mode === 'preview') {
        clone.setAttribute('muted', '');
        const sources = [...clone.querySelectorAll('source')];
        const lightweightSource = sources.find((source) => source.src.includes('.SD-480p-'));
        const preferredPreviewUrl = template.dataset.previewUrl || (lightweightSource && lightweightSource.src) || '';
        const lightweightUrl = preferredPreviewUrl
          .replace('.HD-1080p-2.5Mbps-', '.SD-480p-0.9Mbps-')
          .replace('.HD-720p-1.6Mbps-', '.SD-480p-0.9Mbps-');
        if (lightweightUrl) {
          sources.forEach((source) => source.remove());
          clone.removeAttribute('src');
          clone.src = lightweightUrl;
        }
      } else {
        clone.removeAttribute('muted');
      }
      return clone;
    }

    function startPreview(card, reason) {
      if (!card || dialog && !dialog.hidden || REDUCED_MOTION.matches || SAVE_DATA) return;
      if (reason === 'viewport' && card !== featuredCard) return;
      if (card === featuredCard && featuredPaused && reason !== 'manual') return;
      const hasNative = card.dataset.hasNative === 'true';
      const source = sourceFor(card);
      if (!hasNative && (!source || source.platform !== 'youtube')) return;
      if (!hasNative && root.dataset.youtubePreview !== 'true') return;
      if (!hasNative && reason === 'viewport') return;
      if (activePreview === card) return;
      stopPreview();
      const slot = card.querySelector('[data-hera-kol-preview]');
      if (!slot) return;
      let media = null;
      if (hasNative) {
        media = nativeVideoFor(card, 'preview');
      } else {
        media = iframeFor(youtubeEmbed(source.id, 'preview'), `${card.dataset.title} muted preview`, 'youtube', true);
      }
      if (!media) return;
      activePreview = card;
      activePreviewReason = reason;
      card.classList.add('is-preview-loading');

      const revealPreview = () => {
        if (activePreview !== card || !media.isConnected) return;
        card.classList.remove('is-preview-loading');
        card.classList.add('is-previewing');
        updateFeaturedToggle();
      };

      if (media instanceof HTMLVideoElement) {
        media.addEventListener('playing', revealPreview, { once: true });
      } else {
        media.addEventListener('load', revealPreview, { once: true });
      }
      slot.append(media);
      if (media instanceof HTMLVideoElement) {
        media.play().then(revealPreview).catch(() => {
          if (activePreview === card) stopPreview();
        });
      }
      updateFeaturedToggle();
    }

    function bestVisibleNative() {
      if (activePreviewReason === 'interaction') return;
      if (featuredAutoplay && !featuredPaused && featuredRatio >= FEATURED_PLAY_THRESHOLD && document.visibilityState !== 'hidden') {
        startPreview(featuredCard, 'viewport');
      }
      else if (activePreviewReason === 'viewport') stopPreview();
    }

    function updateFeaturedRatioFromLayout() {
      if (!featuredCard) return;
      const rect = featuredCard.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      featuredRatio = rect.height ? Math.min(1, visibleHeight / rect.height) : 0;
      bestVisibleNative();
    }

    function handleViewportScroll() {
      if (viewportFrame) return;
      viewportFrame = requestAnimationFrame(() => {
        viewportFrame = 0;
        updateFeaturedRatioFromLayout();
      });
    }

    function scheduleInteractionPreview(card) {
      if (!FINE_POINTER.matches && document.activeElement !== card.querySelector('[data-hera-kol-open]')) return;
      window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(() => startPreview(card, 'interaction'), 240);
    }

    function maxRailScroll() {
      return rail ? Math.max(0, rail.scrollWidth - rail.clientWidth) : 0;
    }

    function clampRailLeft(value) {
      return Math.min(maxRailScroll(), Math.max(0, value));
    }

    function clampRailPosition() {
      if (!rail) return;
      const clamped = clampRailLeft(rail.scrollLeft);
      if (Math.abs(clamped - rail.scrollLeft) > 0.5) rail.scrollLeft = clamped;
    }

    function ensureCardVisible(card) {
      if (!rail || !card) return;
      const railRect = rail.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const safeInset = 8;
      let delta = 0;
      if (cardRect.left < railRect.left + safeInset) {
        delta = cardRect.left - railRect.left - safeInset;
      } else if (cardRect.right > railRect.right - safeInset) {
        delta = cardRect.right - railRect.right + safeInset;
      }
      if (Math.abs(delta) < 1) return;
      rail.scrollTo({
        left: clampRailLeft(rail.scrollLeft + delta),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    function setInteractionCard(card) {
      if (!card || interactionCard === card) return;
      interactionCard = card;
      root.classList.add('has-interaction-card');
      cards.forEach((item) => item.classList.toggle('is-interaction-active', item === card));
      window.clearTimeout(interactionLayoutTimer);
      requestAnimationFrame(() => ensureCardVisible(card));
      interactionLayoutTimer = window.setTimeout(() => ensureCardVisible(card), 260);
    }

    function clearInteractionCard(card) {
      if (!interactionCard || card && interactionCard !== card) return;
      if (interactionCard.matches(':hover') || interactionCard.contains(document.activeElement)) return;
      interactionCard.classList.remove('is-interaction-active');
      interactionCard = null;
      root.classList.remove('has-interaction-card');
      window.clearTimeout(interactionLayoutTimer);
    }

    function maybeResumeViewportPreview() {
      window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(() => {
        if (activePreviewReason === 'interaction') stopPreview();
        bestVisibleNative();
      }, 100);
    }

    function visibleRange() {
      if (!rail || !cards.length) return { first: 0, last: 0 };
      const railRect = rail.getBoundingClientRect();
      const visible = cards
        .map((card, index) => ({ index, rect: card.getBoundingClientRect() }))
        .filter(({ rect }) => rect.right > railRect.left + 4 && rect.left < railRect.right - 4);
      return {
        first: visible.length ? visible[0].index : 0,
        last: visible.length ? visible[visible.length - 1].index : 0
      };
    }

    function updateControls(announce = false) {
      if (!rail) return;
      const max = maxRailScroll();
      if (prev) prev.disabled = rail.scrollLeft <= 4;
      if (next) next.disabled = rail.scrollLeft >= max - 4;
      if (announce && status) {
        const range = visibleRange();
        status.textContent = `Showing videos ${range.first + 1}–${range.last + 1} of ${cards.length}.`;
      }
    }

    function closestToRailCenter() {
      if (!rail || !cards.length) return null;
      const railRect = rail.getBoundingClientRect();
      const railCenter = railRect.left + (railRect.width / 2);
      return cards.reduce((bestCard, card) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs((rect.left + (rect.width / 2)) - railCenter);
        const bestRect = bestCard.getBoundingClientRect();
        const bestDistance = Math.abs((bestRect.left + (bestRect.width / 2)) - railCenter);
        return distance < bestDistance ? card : bestCard;
      }, cards[0]);
    }

    function scrollToCard(card, behavior = 'smooth') {
      if (!rail || !card) return;
      const target = card.offsetLeft - ((rail.clientWidth - card.offsetWidth) / 2);
      centeredCard = card;
      rail.scrollTo({ left: clampRailLeft(target), behavior });
    }

    function scrollByCard(direction) {
      if (!rail || !cards.length) return;
      const currentIndex = Math.max(0, cards.indexOf(closestToRailCenter()));
      const targetIndex = Math.min(cards.length - 1, Math.max(0, currentIndex + direction));
      scrollToCard(cards[targetIndex], REDUCED_MOTION.matches ? 'auto' : 'smooth');
      window.setTimeout(() => updateControls(true), REDUCED_MOTION.matches ? 0 : 380);
    }

    function positionFeatured() {
      if (!featuredCard || !rail || initialPositioned) return;
      initialPositioned = true;
      scrollToCard(featuredCard, 'auto');
      updateControls(false);
    }

    function clearPlayer() {
      if (!player) return;
      window.clearTimeout(instagramLoadTimer);
      instagramLoadTimer = 0;
      player.querySelectorAll('video').forEach((video) => video.pause());
      player.querySelectorAll('iframe').forEach((iframe) => { iframe.src = 'about:blank'; });
      player.replaceChildren();
      player.classList.remove('is-loading-instagram');
      if (dialog) dialog.classList.remove('has-actions');
      if (playerActions) {
        playerActions.replaceChildren();
        playerActions.hidden = true;
      }
    }

    function showPlayerMessage(message) {
      const notice = document.createElement('p');
      notice.className = 'hera-kol__player-error';
      notice.textContent = message;
      player.append(notice);
    }

    function showOriginalAction(card) {
      if (!playerActions) return;
      const sourceUrl = card.dataset.sourceUrl || '';
      const platform = instagramCode(sourceUrl)
        ? 'instagram'
        : youtubeId(sourceUrl)
          ? 'youtube'
          : '';
      if (!platform) return;
      const link = document.createElement('a');
      link.className = 'hera-kol__instagram-link hera-kol__original-link';
      link.href = sourceUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = platform === 'instagram' ? 'Open in Instagram' : 'Watch on YouTube';
      playerActions.append(link);
      playerActions.hidden = false;
      dialog.classList.add('has-actions');
    }

    function showInstagramEmbed(source, title) {
      const iframe = iframeFor(instagramEmbed(source.id), title, 'instagram');
      const loading = document.createElement('div');
      loading.className = 'hera-kol__instagram-loading';
      loading.setAttribute('role', 'status');
      loading.innerHTML = '<span aria-hidden="true"></span><p>Loading Instagram video…</p>';
      player.classList.add('is-loading-instagram');
      player.append(iframe, loading);
      iframe.addEventListener('load', () => {
        window.clearTimeout(instagramLoadTimer);
        instagramLoadTimer = 0;
        player.classList.remove('is-loading-instagram');
        loading.remove();
      }, { once: true });
      instagramLoadTimer = window.setTimeout(() => {
        const message = loading.querySelector('p');
        if (message) message.textContent = 'Instagram is taking longer than usual. You can open the Reel below.';
      }, 6000);
    }

    function openDialog(card) {
      if (!dialog || !player) return;
      stopPreview();
      clearPlayer();
      lastFocus = document.activeElement;
      const title = card.dataset.title || 'Watch video';
      const source = sourceFor(card);
      const nativeVideo = nativeVideoFor(card, 'modal');
      dialog.classList.toggle('is-landscape', card.dataset.aspect === 'landscape');

      if (nativeVideo) {
        player.append(nativeVideo);
        nativeVideo.play().catch(() => {});
        showOriginalAction(card);
      } else if (source && source.platform === 'youtube') {
        player.append(iframeFor(youtubeEmbed(source.id, 'modal'), title, 'youtube'));
        showOriginalAction(card);
      } else if (source && source.platform === 'instagram') {
        showInstagramEmbed(source, title);
        showOriginalAction(card);
      } else {
        showPlayerMessage('This video link is missing or is not a supported YouTube or Instagram URL.');
      }

      dialog.hidden = false;
      document.body.classList.add('hera-kol-modal-open');
      requestAnimationFrame(() => dialog.querySelector('.hera-kol__close').focus());
    }

    function closeDialog() {
      if (!dialog || dialog.hidden) return;
      clearPlayer();
      dialog.hidden = true;
      dialog.classList.remove('is-landscape');
      if (!document.querySelector(`${ROOT_SELECTOR} [data-hera-kol-dialog]:not([hidden])`)) {
        document.body.classList.remove('hera-kol-modal-open');
      }
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
      bestVisibleNative();
    }

    function trapDialogFocus(event) {
      if (!dialog || dialog.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialogPanel.querySelectorAll('button:not([disabled]), a[href], iframe, video[controls]')]
        .filter((element) => !element.hidden && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    cards.forEach((card) => {
      addYouTubePoster(card);
      const open = card.querySelector('[data-hera-kol-open]');
      open.addEventListener('click', () => openDialog(card));
      card.addEventListener('pointerenter', () => {
        if (FINE_POINTER.matches) setInteractionCard(card);
        scheduleInteractionPreview(card);
      });
      card.addEventListener('pointerleave', () => {
        window.setTimeout(() => clearInteractionCard(card), 0);
        maybeResumeViewportPreview();
      });
      open.addEventListener('focus', () => {
        setInteractionCard(card);
        scheduleInteractionPreview(card);
      });
      open.addEventListener('blur', () => {
        window.setTimeout(() => clearInteractionCard(card), 0);
        maybeResumeViewportPreview();
      });
    });

    if (featuredToggle) {
      featuredToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isPlaying = activePreview === featuredCard && (
          featuredCard.classList.contains('is-previewing') ||
          featuredCard.classList.contains('is-preview-loading')
        );
        if (isPlaying) {
          featuredPaused = true;
          stopPreview();
        } else {
          featuredPaused = false;
          startPreview(featuredCard, 'viewport');
        }
        updateFeaturedToggle();
      });
    }

    if (featuredAutoplay && 'IntersectionObserver' in window && !SAVE_DATA && !REDUCED_MOTION.matches) {
      viewportObserver = new IntersectionObserver((entries) => {
        const entry = entries.find((item) => item.target === featuredCard);
        if (entry) featuredRatio = entry.isIntersecting ? entry.intersectionRatio : 0;
        bestVisibleNative();
      }, { threshold: [0, 0.35, 0.65, 0.85, 1] });
      viewportObserver.observe(featuredCard);
    }

    if (prev) prev.addEventListener('click', () => scrollByCard(-1));
    if (next) next.addEventListener('click', () => scrollByCard(1));
    if (rail) {
      rail.addEventListener('keydown', (event) => {
        if (event.target !== rail) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollByCard(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollByCard(1);
        }
      });
      rail.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          updateControls(false);
          scrollFrame = 0;
        });
        window.clearTimeout(scrollSettleTimer);
        scrollSettleTimer = window.setTimeout(() => {
          clampRailPosition();
          centeredCard = closestToRailCenter() || centeredCard;
        }, 140);
      }, { passive: true });
    }

    closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
    if (dialog) dialog.addEventListener('keydown', trapDialogFocus);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (activePreview) stopPreview();
      } else {
        bestVisibleNative();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const handleResize = () => {
      updateControls(false);
      window.clearTimeout(resizeTimer);
      window.clearTimeout(resizeSettleTimer);
      resizeTimer = window.setTimeout(() => {
        scrollToCard(interactionCard || featuredCard || centeredCard, 'auto');
        updateFeaturedRatioFromLayout();
      }, 80);
      resizeSettleTimer = window.setTimeout(() => {
        scrollToCard(interactionCard || featuredCard || centeredCard, 'auto');
      }, 340);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleViewportScroll, { passive: true });
    updateFeaturedToggle();
    updateControls(false);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      positionFeatured();
      updateFeaturedRatioFromLayout();
    }));

    root.__heraKolDestroy = () => {
      stopPreview();
      clearPlayer();
      if (viewportObserver) viewportObserver.disconnect();
      window.clearTimeout(scrollSettleTimer);
      window.clearTimeout(resizeTimer);
      window.clearTimeout(resizeSettleTimer);
      window.clearTimeout(interactionLayoutTimer);
      if (viewportFrame) cancelAnimationFrame(viewportFrame);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleViewportScroll);
      root.classList.remove('has-interaction-card');
      cards.forEach((card) => card.classList.remove('is-interaction-active'));
      document.body.classList.remove('hera-kol-modal-open');
    };
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  initAll();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (root && root.__heraKolDestroy) root.__heraKolDestroy();
  });
})();
